// The plan catalogue: the one place every surface (server, panel, onboard, homepage)
// reads plan ids, names, prices and credits from.
export namespace PlanCatalogue {
    export type PlanId = 'free' | 'solo' | 'equipe' | 'escala';
    export type PaidPlanId = Exclude<PlanId, 'free'>;

    export type FreePlan = {
        id: 'free';
        name: string;
        priceInCents: 0;
        includedCredits: null;
    };

    export type PaidPlan = {
        id: PaidPlanId;
        name: string;
        recommendedFor: string;
        priceInCents: number;
        // Quoted per month; every grant is derived from this figure.
        includedCredits: number;
        // Roughly one client-day of conversation per 28k credits, rounded for copy.
        approximateConversationsPerMonth: number;
    };

    export type Plan = FreePlan | PaidPlan;

    export const FREE: FreePlan = {
        id: 'free',
        name: 'Free',
        priceInCents: 0,
        includedCredits: null,
    };

    export const PAID: Record<PaidPlanId, PaidPlan> = {
        solo: {
            id: 'solo',
            name: 'Solo',
            recommendedFor: 'quem atende sozinho ou tem agenda leve',
            priceInCents: 7200,
            includedCredits: 13_000_000,
            approximateConversationsPerMonth: 450,
        },
        equipe: {
            id: 'equipe',
            name: 'Equipe',
            recommendedFor: 'equipes pequenas com agenda cheia',
            priceInCents: 17900,
            includedCredits: 39_000_000,
            approximateConversationsPerMonth: 1400,
        },
        escala: {
            id: 'escala',
            name: 'Escala',
            recommendedFor: 'clínicas e salões de alto volume',
            priceInCents: 47900,
            includedCredits: 117_000_000,
            approximateConversationsPerMonth: 4200,
        },
    };

    export const PLANS: Record<PlanId, Plan> = { free: FREE, ...PAID };

    export const PLAN_IDS = ['free', 'solo', 'equipe', 'escala'] as const satisfies readonly PlanId[];
    export const PAID_PLAN_IDS = ['solo', 'equipe', 'escala'] as const satisfies readonly PaidPlanId[];

    export function isPlanId(value: string): value is PlanId {
        return (PLAN_IDS as readonly string[]).includes(value);
    }

    export function isPaid(id: PlanId): id is PaidPlanId {
        return id !== 'free';
    }

    const priceFormatter = new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    });

    // Whole reais, the way list prices are quoted ("R$ 72").
    export function formatPrice(priceInCents: number): string {
        return priceFormatter.format(priceInCents / 100);
    }
}
